import axios, { type AxiosRequestConfig } from 'axios';
import { resolveFrontendApiBase } from '../utils/apiBase';
import { getAuthHeaders } from '../utils/authHeaders';
import type { Book, SeriesSummary, TranslationProgressEntry } from '../types';
import type { TermScanPreflightState } from './BookDetailPage.types';
import type { RecommendedTranslationWorkflowPolicy } from '../recommendedTranslationWorkflow';

export const BOOK_DETAIL_API_BASE = resolveFrontendApiBase();

type AuthUser = Parameters<typeof getAuthHeaders>[0];

const withAuth = async (currentUser?: AuthUser): Promise<AxiosRequestConfig> => ({
  headers: await getAuthHeaders(currentUser),
});

const mergeAuthConfig = async (
  config?: AxiosRequestConfig,
  currentUser?: AuthUser,
): Promise<AxiosRequestConfig> => {
  const authConfig = await withAuth(currentUser);
  return {
    ...(config || {}),
    headers: {
      ...(authConfig.headers || {}),
      ...(config?.headers || {}),
    },
  };
};

export const isBookDetailApiError = axios.isAxiosError;

export const bookDetailApiGet = async <T = any>(
  url: string,
  config?: AxiosRequestConfig,
) => axios.get<T>(url, await mergeAuthConfig(config));

export const bookDetailApiPost = async <T = any>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
) => axios.post<T>(url, data, await mergeAuthConfig(config));

export const bookDetailApiDelete = async <T = any>(
  url: string,
  config?: AxiosRequestConfig,
) => axios.delete<T>(url, await mergeAuthConfig(config));

export const bookDetailFetch = async (
  input: RequestInfo | URL,
  init?: RequestInit,
) => {
  const headers = new Headers(init?.headers || {});
  const authHeaders = await getAuthHeaders();
  Object.entries(authHeaders).forEach(([key, value]) => headers.set(key, value));
  return fetch(input, { ...(init || {}), headers });
};

export const fetchBookDetail = async ({
  bookId,
  slim = false,
}: {
  bookId: string;
  slim?: boolean;
}): Promise<Book> => {
  const suffix = slim ? '?slim=true' : '';
  const response = await axios.get(
    `${BOOK_DETAIL_API_BASE}/uploadedbooks/book/${bookId}${suffix}`,
    await withAuth(),
  );
  return response.data;
};

export const fetchPreferredTranslationModel = async (): Promise<RecommendedTranslationWorkflowPolicy> => {
  const response = await axios.get(`${BOOK_DETAIL_API_BASE}/api/translation-preferred-model`);
  return {
    ...(response.data || {}),
    modelId: String(response.data?.modelId || '').trim(),
    name: String(response.data?.name || 'legacy'),
  };
};

const userUiFlagsInFlight = new Map<string, Promise<Record<string, any>>>();

export const fetchUserUiFlags = ({
  userId,
}: {
  userId: string;
}): Promise<Record<string, any>> => {
  const pending = userUiFlagsInFlight.get(userId);
  if (pending) return pending;
  const request = (async () => {
    const response = await axios.get(`${BOOK_DETAIL_API_BASE}/user/ui-flags`, {
      params: { userId },
      ...(await withAuth()),
    });
    return response.data || {};
  })().finally(() => userUiFlagsInFlight.delete(userId));
  userUiFlagsInFlight.set(userId, request);
  return request;
};

export const setUserUiFlag = async ({
  userId,
  flag,
  flags,
  value,
}: {
  userId: string;
  flag?: string;
  flags?: Record<string, unknown>;
  value?: unknown;
}) => {
  return axios.post(
    `${BOOK_DETAIL_API_BASE}/user/ui-flags/set`,
    {
      userId,
      ...(flags ? { flags } : { flag, value }),
    },
    await withAuth(),
  );
};

export const fetchSeriesOptions = async ({
  userId,
}: {
  userId: string;
}): Promise<SeriesSummary[]> => {
  const response = await axios.get(`${BOOK_DETAIL_API_BASE}/series`, {
    params: { user_id: userId },
  });
  return response.data || [];
};

export const fetchSeriesTranslateRules = async ({
  seriesId,
  userId,
}: {
  seriesId: string;
  userId: string;
}): Promise<{ alwaysTranslate: Record<string, string[]>; neverTranslate: Record<string, string[]> }> => {
  const response = await bookDetailApiGet(`${BOOK_DETAIL_API_BASE}/series/${seriesId}/translate-rules`, {
    params: { user_id: userId },
  });
  return {
    alwaysTranslate: response.data?.alwaysTranslate || {},
    neverTranslate: response.data?.neverTranslate || {},
  };
};

export const fetchTermScanPreflight = async ({
  bookId,
  userId,
  sourceLang,
  targetLang,
  autoRunEnabled,
}: {
  bookId: string;
  userId: string;
  sourceLang: string;
  targetLang: string;
  autoRunEnabled?: boolean;
}): Promise<TermScanPreflightState> => {
  const response = await axios.get(`${BOOK_DETAIL_API_BASE}/books/${bookId}/term-scan/preflight`, {
    params: { userId, sourceLang, targetLang, autoRunEnabled },
  });
  return response.data;
};

export const startTermScan = async ({
  bookId,
  userId,
  sourceLang,
  targetLang,
  currentUser,
}: {
  bookId: string;
  userId: string;
  sourceLang: string;
  targetLang: string;
  currentUser?: AuthUser;
}) => {
  return axios.post(
    `${BOOK_DETAIL_API_BASE}/books/${bookId}/term-scan`,
    { userId, sourceLang, targetLang },
    await withAuth(currentUser),
  );
};

export const autoApplyConfidentTermScan = async ({
  bookId,
  runId,
  userId,
  sourceLang,
  targetLang,
  confidenceThreshold = 0.85,
}: {
  bookId: string;
  runId: string;
  userId: string;
  sourceLang: string;
  targetLang: string;
  confidenceThreshold?: number;
}) => {
  return axios.post(`${BOOK_DETAIL_API_BASE}/books/${bookId}/term-scan/${runId}/auto-apply-confident`, {
    userId,
    sourceLang,
    targetLang,
    confidenceThreshold,
  });
};

export const refreshTranslationProgressForBook = async ({
  bookId,
  userId,
  currentUser,
}: {
  bookId: string;
  userId: string;
  currentUser?: AuthUser;
}): Promise<Record<string, TranslationProgressEntry>> => {
  const response = await axios.post<Record<string, TranslationProgressEntry>>(
    `${BOOK_DETAIL_API_BASE}/translation-progress/refresh`,
    { bookId, userId },
    await withAuth(currentUser),
  );
  return response.data || {};
};

export const requestExportDownloadUrl = async ({
  exportId,
  userId,
  currentUser,
}: {
  exportId: string;
  userId: string;
  currentUser?: AuthUser;
}): Promise<string | null> => {
  const response = await axios.post(
    `${BOOK_DETAIL_API_BASE}/export-download-url`,
    { export_id: exportId, user_id: userId },
    await withAuth(currentUser),
  );
  return response.data?.downloadUrl || response.data?.url || null;
};

export const refreshBookWordCounts = async ({
  bookId,
  userId,
  force = false,
  currentUser,
}: {
  bookId: string;
  userId: string;
  force?: boolean;
  currentUser?: AuthUser;
}) => {
  return axios.post(
    `${BOOK_DETAIL_API_BASE}/wordcount/recalculate-book`,
    { book_id: bookId, user_id: userId, force },
    await withAuth(currentUser),
  );
};

export type TranslatedChapterAvailability =
  | { ready: false }
  | { ready: true; chapter: Record<string, any> };

export const fetchTranslatedChapterAvailability = async ({
  bookId, chapterGuid, sourceLang, targetLang, reason,
}: { bookId: string; chapterGuid: string; sourceLang: string; targetLang: string; reason: string }): Promise<TranslatedChapterAvailability> => {
  const segment = encodeURIComponent;
  const response = await bookDetailApiGet(
    `${BOOK_DETAIL_API_BASE}/uploadedbooks/book/${segment(bookId)}/translations/${segment(sourceLang)}/${segment(targetLang)}/chapters/${segment(chapterGuid)}`,
    { params: { allow_missing: true }, headers: { 'X-ScribeShadow-Hydration-Reason': reason } },
  );
  const data = response.data;
  if (data?.chapter_guid !== chapterGuid) throw new Error('Invalid translated chapter response.');
  if (data.status === 'not_ready') {
    if (data.source_language !== sourceLang || data.target_language !== targetLang) {
      throw new Error('Invalid translated chapter response.');
    }
    return { ready: false };
  }
  if (data.status != null && data.status !== 'ready') throw new Error('Invalid translated chapter response.');
  if (typeof data.translated_text !== 'string') throw new Error('Invalid translated chapter response.');
  return { ready: true, chapter: data };
};
